// src/pages/Deals.jsx
import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { dealAPI, categoryAPI } from '../services/api';
import DealCard from '../components/deals/DealCard';
import Loading from '../components/common/Loading';
import { FiFilter, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { toast } from 'react-toastify';

const readTotalFromResponse = (res) => {
  try {
    const payload = res?.data ?? {};
    if (payload.totalItems ?? payload.total) return Number(payload.totalItems ?? payload.total);
    if (payload.meta && (payload.meta.total ?? payload.meta.totalItems)) return Number(payload.meta.total ?? payload.meta.totalItems);
    if (payload.pagination && (payload.pagination.total ?? payload.pagination.totalItems)) return Number(payload.pagination.total ?? payload.pagination.totalItems);
    if (payload.data && (payload.data.total ?? payload.data.totalItems)) return Number(payload.data.total ?? payload.data.totalItems);
    const headerTotal = res?.headers?.['x-total-count'] || res?.headers?.['X-Total-Count'];
    if (headerTotal) return Number(headerTotal);
  } catch (e) {}
  return 0;
};

const Deals = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const qpString = searchParams.toString();

  const initialPage = Number(searchParams.get('page') || 1);
  const initialLimit = Number(searchParams.get('limit') || 12);

  const initialFilters = {
    category: searchParams.get('category') || '',
    city: searchParams.get('city') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    minDiscount: searchParams.get('minDiscount') || '',
    sort: searchParams.get('sort') || 'newest',
    search: searchParams.get('search') || searchParams.get('q') || ''

  };

  const [deals, setDeals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState(initialFilters);

  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setFilters({
      category: searchParams.get('category') || '',
      city: searchParams.get('city') || '',
      minPrice: searchParams.get('minPrice') || '',
      maxPrice: searchParams.get('maxPrice') || '',
      minDiscount: searchParams.get('minDiscount') || '',
      sort: searchParams.get('sort') || 'newest',
      search: searchParams.get('search') || searchParams.get('q') || ''

    });
    setPage(Number(searchParams.get('page') || 1));
    setLimit(Number(searchParams.get('limit') || 12));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qpString]);

  const buildParams = (f, p = page, l = limit) => {
    const out = {};
    Object.keys(f).forEach(k => {
      const v = f[k];
      if (v !== undefined && v !== null && String(v).trim() !== '') out[k] = v;
    });
    out.page = p;
    out.limit = l;
    return out;
  };

  const loadDeals = useCallback(async (p = page, l = limit, f = filters) => {
    setLoading(true);
    try {
      const params = buildParams(f, p, l);
      const res = await dealAPI.getAll(params);
      const payload = res?.data ?? res;
      const list = Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : []);

      setDeals(list);
      const t = readTotalFromResponse(res);
      setTotal(t || list.length || 0);

      const serverPage = payload.currentPage ?? payload.page ?? payload.data?.currentPage ?? null;
      const serverLimit = payload.itemsPerPage ?? payload.limit ?? payload.data?.itemsPerPage ?? null;
      if (serverPage) setPage(Number(serverPage));
      if (serverLimit) setLimit(Number(serverLimit));
    } catch (err) {
      console.error('Erreur chargement deals:', err);
      toast.error('Erreur lors du chargement des deals');
      setDeals([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters]);

  const loadCategories = useCallback(async () => {
    try {
      const res = await categoryAPI.getAll();
      const payload = res?.data ?? res;
      setCategories(payload?.data ?? payload ?? []);
    } catch (err) {
      console.error('Erreur chargement catégories:', err);
    }
  }, []);

  useEffect(() => { loadDeals(page, limit, filters); }, [loadDeals, page, limit, filters]);
  useEffect(() => { loadCategories(); }, [loadCategories]);

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    const params = new URLSearchParams();
    const toSend = buildParams(filters, 1, limit);
    Object.entries(toSend).forEach(([k, v]) => params.set(k, v));
    setSearchParams(params, { replace: true });
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ category: '', city: '', minPrice: '', maxPrice: '', minDiscount: '', sort: 'newest', search: '' });
    setSearchParams({}, { replace: true });
    setPage(1);
    setLimit(12);
  };

  const totalPages = useMemo(() => Math.max(1, Math.ceil((total || deals.length) / (limit || 1))), [total, deals.length, limit]);
  const goToPage = (p) => { const next = Math.max(1, Math.min(totalPages, p)); if (next === page) return; setPage(next); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Tous les Deals</h1>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className="md:hidden flex items-center space-x-2 bg-primary-600 text-white px-4 py-2 rounded-lg"
        >
          <FiFilter />
          <span>Filtres</span>
        </button>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        <aside className={`md:w-64 ${showFilters ? 'block' : 'hidden md:block'}`}>
          <div className="bg-white p-4 rounded-lg shadow-md sticky top-20">
            <h3 className="font-semibold text-lg mb-4">Filtres</h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Catégorie</label>
              <select value={filters.category} onChange={(e) => handleFilterChange('category', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2">
                <option value="">Toutes les catégories</option>
                {categories.map(cat => (
                  <option key={cat._id} value={cat.slug ?? cat._id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Prix (MAD)</label>
              <div className="flex space-x-2">
                <input type="number" placeholder="Min" value={filters.minPrice} onChange={(e) => handleFilterChange('minPrice', e.target.value)} className="w-1/2 border border-gray-300 rounded-lg px-3 py-2" />
                <input type="number" placeholder="Max" value={filters.maxPrice} onChange={(e) => handleFilterChange('maxPrice', e.target.value)} className="w-1/2 border border-gray-300 rounded-lg px-3 py-2" />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Réduction minimum (%)</label>
              <input type="number" value={filters.minDiscount} onChange={(e) => handleFilterChange('minDiscount', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Trier par</label>
              <select value={filters.sort} onChange={(e) => handleFilterChange('sort', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2">
                <option value="newest">Plus récents</option>
                <option value="price-asc">Prix croissant</option>
                <option value="price-desc">Prix décroissant</option>
                <option value="discount">Meilleure réduction</option>
                <option value="popular">Populaires</option>
              </select>
            </div>

            <div className="flex space-x-2">
              <button onClick={applyFilters} className="flex-1 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700">Appliquer</button>
              <button onClick={clearFilters} className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300">Effacer</button>
            </div>
          </div>
        </aside>

        <div className="flex-1">
          {loading ? (
            <Loading />
          ) : deals.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">Aucun deal trouvé</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <p className="text-gray-600">{total} résultat{total > 1 ? 's' : ''}</p>
                <div className="flex items-center space-x-3">
                  <div className="text-sm text-gray-600">Afficher</div>
                  <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }} className="border px-2 py-1 rounded">
                    {[6, 12, 24, 48].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {deals.map(deal => (
                  <DealCard key={deal._id ?? deal.id} deal={deal} />
                ))}
              </div>

              <div className="flex items-center justify-between mt-6">
                <div className="flex items-center space-x-2">
                  <button type="button" onClick={() => goToPage(page - 1)} disabled={page <= 1 || loading} className="px-3 py-2 border rounded disabled:opacity-50 flex items-center space-x-1"><FiChevronLeft /><span>Préc</span></button>
                  <div className="text-sm text-gray-600">Page <strong>{page}</strong> / <strong>{totalPages}</strong></div>
                  <button type="button" onClick={() => goToPage(page + 1)} disabled={page >= totalPages || loading} className="px-3 py-2 border rounded disabled:opacity-50 flex items-center space-x-1"><span>Suiv</span><FiChevronRight /></button>
                </div>

                <div className="text-sm text-gray-600">{total > 0 ? `Total: ${total} résultats` : `${deals.length} affichés`}</div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Deals;